import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Button,
  FormGroup,
  Input,
  Label,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap'
import ErrorBoundary from 'components/ErrorBoundary'
import Loading from 'components/Loading/Loading'
import SchoolLookUp from 'components/SchoolLookUp'
import Select from 'components/Select'
import CustomCheckBox from 'components/CustomCheckBox'
import { jobTitles } from 'lib/meta'
import {
  CurrentUser,
  RootState,
  SchoolLookUpSiteData,
  UpdateUserProfileRequest,
} from 'types'
import { saveAndHandleProductUser } from 'store/css-admin/userManager/operations'
import { closeModal as closeModalAction } from 'store/modal'

const Tabs = {
  Personal: 1,
  Organization: 4,
}

const isNotNullOrEmpty = (str?: string): boolean => Boolean(str?.length)

type EditUser = Omit<CurrentUser, 'archetypes'> & {
  archetypes: Map<number, any>
}

interface EditProductUserModalProps {
  closeModal?: () => void
}

const EditProductUserModal: React.FC<EditProductUserModalProps> = ({
  closeModal,
}) => {
  const dispatch = useDispatch()
  const [activeTab, setActiveTab] = useState(Tabs.Personal)
  const [isDirty, setIsDirty] = useState(false)
  const [editUser, setEditUser] = useState<EditUser | null>(null)
  const [siteData, setSiteData] = useState<SchoolLookUpSiteData>({
    city: '',
    country: '',
    region: '',
    netsuiteCustomerId: null,
    organizationName: '',
    companyName: '',
    nsCustomerId: '',
  })

  const { archetypes, isSaving, isLoading, productUser } = useSelector(
    (state: RootState) => ({
      archetypes: state.userManager?.archetypes?.data || null,
      isSaving: state.userManager?.isSaving || false,
      isLoading: state.userManager?.isLoading || false,
      productUser: state.userManager?.productUser || null,
    }),
  )

  const handleCloseModal = useCallback(() => {
    closeModal ? closeModal() : dispatch(closeModalAction())
  }, [dispatch, closeModal])

  useEffect(() => {
    if (productUser) {
      const archetypesMap = new Map(
        (productUser.archetypes || []).map((archetype: any) => [
          archetype.id,
          archetype,
        ]),
      )

      setEditUser({ ...productUser, archetypes: archetypesMap })
      setSiteData({
        country: productUser.country || '',
        region: productUser.region || '',
        city: productUser.city || '',
        netsuiteCustomerId: productUser.netSuiteCustomerId ?? null,
        organizationName: productUser.siteName || '',
        companyName: productUser.companyName || '',
        nsCustomerId: productUser.nsCustomerId || '',
      })
    }
  }, [productUser])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!editUser) return
      setEditUser({ ...editUser, [e.target.name]: e.target.value })
      setIsDirty(true)
    },
    [editUser],
  )

  const handleSchoolSelection = useCallback(
    (selectedSiteData: SchoolLookUpSiteData) => {
      setSiteData(selectedSiteData)
      setIsDirty(true)
    },
    [],
  )

  const handleArchetypeSelection = useCallback(
    (archetype: any, hasArchetype: boolean) => {
      if (!editUser) return
      const updatedMap = new Map(editUser.archetypes)
      if (hasArchetype) {
        updatedMap.delete(archetype.id)
      } else {
        updatedMap.set(archetype.id, archetype)
      }
      setEditUser({ ...editUser, archetypes: updatedMap })
      setIsDirty(true)
    },
    [editUser],
  )

  const saveUser = useCallback(async () => {
    if (!editUser) return

    const siteName =
      siteData.netsuiteCustomerId === 0 || siteData.netsuiteCustomerId === null
        ? siteData.organizationName
        : siteData.companyName

    const profileDataToUpdate: UpdateUserProfileRequest = {
      id: editUser.id,
      archetypes: Array.from(editUser.archetypes.keys()),
      city: siteData.city,
      country: siteData.country,
      email: editUser.email,
      firstName: editUser.firstName || '',
      lastName: editUser.lastName || '',
      netsuiteCustomerId: siteData.netsuiteCustomerId ?? 0,
      region: siteData.region || '',
      siteName: siteName || '',
      title: editUser.title || '',
    }

    await dispatch(saveAndHandleProductUser(profileDataToUpdate))
    handleCloseModal()
  }, [dispatch, editUser, siteData, handleCloseModal])

  const isValid = useMemo(() => {
    if (!editUser || !isDirty) return false
    return (
      isNotNullOrEmpty(editUser.firstName) &&
      isNotNullOrEmpty(editUser.lastName) &&
      isNotNullOrEmpty(editUser.title) &&
      editUser.archetypes.size > 0
    )
  }, [editUser, isDirty])

  if (!editUser) return null

  const { email, firstName, isSSO, lastName, title } = editUser

  return (
    <ErrorBoundary>
      <Loading isLoading={isLoading} />
      <Loading isLoading={isSaving} message="Saving..." />
      <ModalHeader toggle={handleCloseModal}>Edit User</ModalHeader>
      <ModalBody>
        <Nav justified tabs>
          <NavItem>
            <NavLink
              active={activeTab === Tabs.Personal}
              onClick={() => setActiveTab(Tabs.Personal)}
            >
              Personal
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              active={activeTab === Tabs.Organization}
              onClick={() => setActiveTab(Tabs.Organization)}
            >
              Organization
            </NavLink>
          </NavItem>
        </Nav>
        <br />
        <TabContent activeTab={activeTab}>
          <TabPane tabId={Tabs.Personal}>
            <p className="float-right">* Required Field</p>
            <FormGroup>
              <Label for="firstName">First Name</Label>
              <Input
                id="firstName"
                name="firstName"
                onChange={handleChange}
                readOnly={isSSO}
                required
                value={firstName || ''}
              />
              {!isNotNullOrEmpty(firstName) && (
                <span className="text-danger">first name is required </span>
              )}
            </FormGroup>
            <FormGroup>
              <Label for="lastName">Last Name</Label>
              <Input
                id="lastName"
                name="lastName"
                onChange={handleChange}
                readOnly={isSSO}
                required
                value={lastName || ''}
              />
              {!isNotNullOrEmpty(lastName) && (
                <span className="text-danger"> last name is required </span>
              )}
            </FormGroup>
            <FormGroup>
              <Label for="displayName">Email</Label>
              <Input
                disabled={true}
                id="email"
                name="email"
                onChange={handleChange}
                value={email || ''}
              />
            </FormGroup>
            <FormGroup>
              <Select
                data={jobTitles}
                label="Job Title&nbsp;*"
                name="title"
                onChange={handleChange}
                value={title || ''}
              />
              {!isNotNullOrEmpty(title) && (
                <span className="text-danger"> job title is required </span>
              )}
            </FormGroup>
            <FormGroup>
              <Label>Archetype</Label>
              {archetypes &&
                archetypes.map((archetype: any) => {
                  const hasArchetype = editUser.archetypes.has(archetype.id)
                  return (
                    <CustomCheckBox
                      checked={hasArchetype}
                      key={`edit-user-profile-archetype-${archetype.id}`}
                      name={archetype.archetypeName}
                      onChange={() =>
                        handleArchetypeSelection(archetype, hasArchetype)
                      }
                      text={archetype.description}
                    />
                  )
                })}
              {editUser.archetypes.size <= 0 && (
                <span className="text-danger">
                  An archetype must be selected
                </span>
              )}
            </FormGroup>
          </TabPane>
        </TabContent>
        <TabContent activeTab={activeTab}>
          <TabPane tabId={Tabs.Organization}>
            <SchoolLookUp
              fireOnLoad={false}
              onSelection={handleSchoolSelection}
              siteData={siteData}
            />
          </TabPane>
        </TabContent>
      </ModalBody>
      <ModalFooter>
        <Button color="link" onClick={handleCloseModal}>
          Cancel
        </Button>
        <Button
          color="success"
          disabled={!isValid || isSaving}
          onClick={saveUser}
        >
          Save
        </Button>
      </ModalFooter>
    </ErrorBoundary>
  )
}

export default EditProductUserModal
